import { Link, useLocation } from 'react-router-dom';
import { Book, Wand2, Swords, Home, Users, Info, MousePointer, Settings } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { LogoutButton } from './auth/LogoutButton';

const Navigation = () => {
  const location = useLocation();
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const { user, loading } = useAuth();
  
  // Function to determine if a path is active
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };
  
  // Common class for navigation links with hover and active states
  const navLinkClass = (path: string) => `
    flex items-center space-x-2 
    px-3 py-2 rounded-md 
    transition-colors duration-200
    ${isActive(path) 
      ? 'bg-primary/10 text-primary font-medium' 
      : 'hover:bg-secondary hover:text-primary'}
  `;
  
  // Toggle admin dropdown menu
  const toggleAdminMenu = () => {
    setShowAdminMenu(!showAdminMenu);
  };
  
  return (
    <nav className="fixed top-0 left-0 right-0 bg-background/80 backdrop-blur-sm border-b z-50" data-testid="navigation">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity" data-testid="nav-logo">
            <span className="font-poetry font-semibold text-xl">Moose Poems</span>
          </Link>
          
          <div className="flex items-center space-x-2">
            <Link to="/" className={navLinkClass('/')} data-testid="nav-home">
              <Home size={20} />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <Link to="/book" className={navLinkClass('/book')} data-testid="nav-book">
              <Book size={20} />
              <span className="hidden sm:inline">Book</span>
            </Link>
            <Link to="/deathmatch" className={navLinkClass('/deathmatch')} data-testid="nav-deathmatch">
              <Swords size={20} />
              <span className="hidden sm:inline">Deathmatch</span>
            </Link>
            <Link to="/generate" className={navLinkClass('/generate')} data-testid="nav-generate">
              <Wand2 size={20} />
              <span className="hidden sm:inline">Create</span>
            </Link>
            <Link to="/poets" className={navLinkClass('/poets')} data-testid="nav-poets">
              <Users size={20} />
              <span className="hidden sm:inline">Poets</span>
            </Link>
            <Link to="/about" className={navLinkClass('/about')} data-testid="nav-about">
              <Info size={20} />
              <span className="hidden sm:inline">About</span>
            </Link>
            
            {/* Conditional Admin Dropdown & Logout Button (TEMPORARY PLACEMENT) */}
            {!loading && user && (
              <div className="relative">
                <button 
                  onClick={toggleAdminMenu}
                  className={`
                    flex items-center space-x-2 
                    px-3 py-2 rounded-md 
                    transition-colors duration-200
                    ${isActive('/admin') || isActive('/tooltip-demo')
                      ? 'bg-primary/10 text-primary font-medium' 
                      : 'hover:bg-secondary hover:text-primary'}
                  `}
                  data-testid="nav-admin"
                >
                  <Settings size={20} />
                  <span className="hidden sm:inline">Admin</span>
                </button>
                
                {showAdminMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-background rounded-md shadow-lg border z-50">
                    <Link 
                      to="/admin/prompts" 
                      className="block px-4 py-2 text-sm hover:bg-secondary"
                      onClick={() => setShowAdminMenu(false)}
                    >
                      Prompt Templates
                    </Link>
                    <Link 
                      to="/admin/highlights"
                      className="block px-4 py-2 text-sm hover:bg-secondary"
                      onClick={() => setShowAdminMenu(false)}
                    >
                      Manage Poem Data
                    </Link>
                    <Link 
                      to="/admin/manage-data"
                      className="block px-4 py-2 text-sm hover:bg-secondary rounded-b-md"
                      onClick={() => setShowAdminMenu(false)}
                    >
                      Manage Poet Data
                    </Link>
                  </div>
                )}
              </div>
            )}
            {!loading && user && <LogoutButton />}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
